import { FREE_SAMPLE } from '../lib/free-tier';

/**
 * Single canonical source of truth for public-facing product facts:
 * - Pricing tiers, interval display strings, and savings metrics
 * - Free sample allowance (distinguishing Phase A sender analysis from Phase B review)
 * - Mobile app availability (Android on Google Play, iOS mobile web)
 * - Obsolete values list used by verification tests to prevent regressions
 */

export const PUBLIC_PRICING = {
  currency: 'USD',
  monthly: {
    amount: 5.99,
    formatted: '$5.99',
    perMonth: '$5.99/month',
    interval: 'month' as const,
  },
  annual: {
    amount: 47.88,
    formatted: '$47.88',
    perYear: '$47.88/year',
    monthlyEquivalent: '$3.99/month',
    monthlyEquivalentAmount: 3.99,
    interval: 'year' as const,
    savingsDollars: 24,
    savingsPercent: 33,
    savingsBadge: 'Save 33%',
    savingsComparison: 'save 33% vs monthly',
    savingsComparisonUppercase: 'SAVE 33% VS MONTHLY',
  },
} as const;

export const PUBLIC_FREE_ALLOWANCE = {
  /** Top senders analyzed during initial cold-start sampling. */
  topSendersAnalyzed: FREE_SAMPLE.senderCap,
  /** User-facing classified sample size reviewed by the user across 3 tracks. */
  emailsClassifiedReview: FREE_SAMPLE.classifyTotal,
  /** Emails classified per top sender. */
  classifyPerSender: FREE_SAMPLE.classifyPerSender,
  /** Short label for the user-facing reviewed sample. */
  reviewCapLabel: `${FREE_SAMPLE.classifyTotal} emails`,
  /** Internal backend fetch ceiling across top senders for sender clutter stats (disclosed in legal terms). */
  backendFetchCeiling: FREE_SAMPLE.fetchTotal,
  /** Deprecated alias for backendFetchCeiling to preserve backward compatibility. */
  emailsAnalyzedCeiling: FREE_SAMPLE.fetchTotal,
  /** Canonical public summary of the free sample allowance. */
  summary: `Sample review of ${FREE_SAMPLE.classifyTotal} emails across top ${FREE_SAMPLE.senderCap} senders in Inbox, Archive, and Trash`,
  /** Standard marketing sentence describing the no-card free sample offer. */
  marketingSentence: `No-card Free scan offers a sample review of ${FREE_SAMPLE.classifyTotal} emails across your top ${FREE_SAMPLE.senderCap} senders in Inbox, Archive, and Trash.`,
} as const;

export const PUBLIC_MOBILE_APPS = {
  android: {
    available: true,
    store: 'Google Play',
    url: 'https://play.google.com/store/apps/details?id=ai.purebox.twa',
    packageId: 'ai.purebox.twa',
    type: 'Trusted Web Activity (Google Play)',
    displayLabel: 'Android app on Google Play',
  },
  ios: {
    available: false,
    nativeAppAvailable: false,
    webAppAvailable: true,
    displayLabel: 'Mobile web for iOS',
  },
  browserExtension: {
    required: false,
    available: false,
  },
  matrixRowLabel: 'Android app (Google Play) & mobile web; no native iOS app yet',
  accessModelLabel: 'Web and Android app on Google Play (no browser extension, no separate inbox; iOS is web-only)',
} as const;

export const OBSOLETE_PUBLIC_FACTS = {
  /** Historical prices that must never appear as PureBox's current checkout price. */
  deprecatedPureBoxPrices: [
    '$9.99',
    '$7.99',
    '$59.90',
    '$54.90',
    '$4.99',
    '$39.90',
  ],
  /** Misleading claims about mobile availability. */
  deprecatedMobileClaims: [
    'no mobile app',
    'no native app yet',
    'has no native mobile app',
  ],
  /** Deprecated marketing claims that confuse the free sample with a 1,000-email cleanup ceiling. */
  deprecatedFreeAllowanceClaims: [
    'hard ceiling of 1,000 emails',
    'capped at 1,000 emails, no automation',
    'hard cap of 1,000 emails',
    'scan of up to 1,000 emails',
  ],
} as const;

export const PUBLIC_FACTS = {
  pricing: PUBLIC_PRICING,
  freeAllowance: PUBLIC_FREE_ALLOWANCE,
  mobile: PUBLIC_MOBILE_APPS,
  obsolete: OBSOLETE_PUBLIC_FACTS,
} as const;
